import * as Sentry from '@sentry/react';
import { captureError } from '../utils/errorHandler';
import { config } from './config';
import { SupportedLanguage } from './i18n';

export class ApiError extends Error {
  public readonly status: number;
  public readonly type: string;

  constructor(message: string, status: number, type: string) {
    super(message);
    this.name = 'ApiError';
    this.type = type;
    this.status = status;

    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export interface ApiRequestParams {
  endpoint: string;
  service: 'chat' | 'survey';
  method?: 'POST' | 'GET' | 'PUT';
  body?: Record<string, unknown> | Record<string, unknown>[] | string | null;
  token?: string | null;
  language?: SupportedLanguage;
}

export interface AuthenticatedApiRequestParams extends Omit<ApiRequestParams, 'token'> {
  token: string; // Token is required for authenticated requests
}

export interface CreateMessageRequestParams {
  endpoint: string;
  method: string;
  headers: Record<string, string>;
  body: string;
  abortSignal?: AbortSignal;
  errorMessage: string;
  sentry?: boolean;
}

// Interface for authenticatedFileUploadRequest parameters
export interface AuthenticatedFileUploadRequestParams {
  endpoint: string;
  service: 'chat' | 'survey';
  method?: 'POST' | 'PUT';
  body: FormData;
  token: string;
  language?: SupportedLanguage;
  headers?: Record<string, string>;
  responseType?: 'json' | 'blob';
}

// Shared error handling logic
const handleApiError = (
  error: unknown,
  endpoint: string,
  method: string,
  requestBody: unknown
): never => {
  const httpMethod = method as 'POST' | 'GET' | 'PUT';

  // If the error is an instance of ApiError, capture and throw it directly
  if (error instanceof ApiError) {
    captureError(error, endpoint, httpMethod, {
      requestBody,
      originalError: error,
      errorStack: error.stack,
      errorMessage: error.message,
    });

    throw error;
  }

  // Handle network errors
  // When making an API request with fetch(), a TypeError usually means one of the following:
  // •	Network failure (e.g., no internet connection, server is unreachable).
  // •	CORS issues (blocked due to cross-origin restrictions).
  // •	Invalid request URL (bad domain, typo in URL).
  // •	Fetch was aborted (AbortController canceled the request).
  // •	SSL issues (invalid certificate, HTTPS issues).
  if (error instanceof TypeError) {
    const isOffline = !navigator.onLine;
    const errorMessage = isOffline ? 'No internet connection' : 'Network error';

    const apiError = new ApiError(errorMessage, 500, 'network_error');

    captureError(apiError, endpoint, httpMethod, {
      requestBody,
      originalError: error,
      errorStack: error.stack,
      errorMessage: error.message,
    });

    throw apiError;
  }

  // Handle syntax errors
  // When making an API request with fetch(), a SyntaxError usually means one of the following:
  // •	Invalid JSON response from the server.
  if (error instanceof SyntaxError) {
    const apiError = new ApiError('Invalid response format', 400, 'syntax_error');

    captureError(apiError, endpoint, httpMethod, {
      requestBody,
      originalError: error,
      errorStack: error.stack,
      errorMessage: error.message,
    });

    throw apiError;
  }

  // Handle other network or parsing errors
  if (error instanceof Error) {
    const apiError = new ApiError(error.message || 'Network error', 500, 'network_error');

    captureError(apiError, endpoint, httpMethod, {
      requestBody,
      originalError: error,
      errorStack: error.stack,
      errorMessage: error.message,
    });

    throw apiError;
  }

  // For all other errors, throw a fatal error
  const fatalError = new ApiError('Unexpected error', 500, 'fatal_error');

  captureError(fatalError, endpoint, httpMethod, {
    requestBody,
    rawError: JSON.stringify(error),
  });

  throw fatalError;
};

// Build request headers
const buildRequestHeaders = (
  token?: string | null,
  language: SupportedLanguage = 'fi',
  customHeaders: Record<string, string> = {},
  includeContentType: boolean = true
): Record<string, string> => {
  const headers: Record<string, string> = {
    ...customHeaders,
  };

  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  headers['X-App-Language'] = language;

  return headers;
};

// Build request URL
const buildChatRequestUrl = (endpoint: string): string => {
  return `${config.apiUrl}${endpoint}`;
};

const buildSurveyRequestUrl = (endpoint: string): string => {
  return `${config.surveyApiUrl}${endpoint}`;
};

// Handle API response
const handleApiResponse = async (response: Response, responseType: 'json' | 'blob' = 'json') => {
  if (!response.ok) {
    throw new ApiError(
      `API request failed with status ${response.status}`,
      response.status,
      'api_error'
    );
  }

  if (responseType === 'blob') {
    return await response.blob();
  }

  return await response.json();
};

// Helper function for making API requests
export const apiRequest = async ({
  endpoint,
  service,
  method = 'GET',
  body = null,
  token = null,
  language = 'fi',
}: ApiRequestParams) => {
  const headers = buildRequestHeaders(token, language);

  const options = {
    body: body ? JSON.stringify(body) : undefined,
    method,
    headers,
  };

  let urlWithPath = '';

  if (service === 'survey') {
    // For survey service, use the survey API URL
    urlWithPath = buildSurveyRequestUrl(endpoint);
  } else if (service === 'chat') {
    // For chat service, use the main API URL
    urlWithPath = buildChatRequestUrl(endpoint);
  }

  try {
    const response = await fetch(urlWithPath, options);
    return handleApiResponse(response);
  } catch (error: unknown) {
    return handleApiError(error, endpoint, method, body);
  }
};

// Helper function for making authenticated API requests
export const authenticatedApiRequest = async ({
  endpoint,
  service,
  method = 'GET',
  body = null,
  token,
  language,
}: AuthenticatedApiRequestParams) => {
  return apiRequest({
    endpoint,
    service,
    method,
    body,
    token,
    language,
  });
};

// Custom function for message creation with special error handling,
// because we want to handle the abort signal and always return an error object instead of throwing
export const createMessageRequest = async ({
  endpoint,
  method,
  headers,
  body,
  abortSignal,
  errorMessage,
}: CreateMessageRequestParams) => {
  try {
    // Extract endpoint from the full URL
    const response = await fetch(`${config.apiUrl}${endpoint}`, {
      method,
      headers,
      body,
      signal: abortSignal,
    });

    if (!response.ok) {
      let errorData: object;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: 'Could not parse error response' };
      }

      // Capture detailed error in Sentry
      if (config.useSentry) {
        Sentry.withScope((scope) => {
          scope.setExtra('endpoint', endpoint);
          scope.setExtra('method', method);
          scope.setExtra('status', response.status);
          scope.setExtra('errorData', errorData);
          scope.setExtra('requestBody', JSON.parse(body));
          Sentry.captureException(new Error(`Request failed: ${response.status}`));
        });
      }

      // Return error object
      return {
        error: true,
        errorData,
        status: response.status,
        message: errorMessage,
      };
    }

    return await response.json();
  } catch (error: unknown) {
    // If request was aborted, return a special response
    if (error instanceof DOMException && error.name === 'AbortError') {
      return { aborted: true };
    }

    // For all other errors, capture in Sentry and return error message
    if (config.useSentry) {
      Sentry.withScope((scope) => {
        scope.setExtra('endpoint', endpoint);
        scope.setExtra('method', method);
        scope.setExtra('error', error);
        Sentry.captureException(new Error('Create message request failed'));
      });
    }

    // Return error object
    return {
      error: true,
      message: errorMessage,
      networkError: true,
    };
  }
};

// Helper function for making authenticated file upload requests
export const authenticatedFileUploadRequest = async ({
  endpoint,
  service,
  method = 'POST',
  body,
  token,
  language = 'fi',
  headers: customHeaders = {},
  responseType = 'json',
}: AuthenticatedFileUploadRequestParams) => {
  // Don't set Content-Type for FormData - browser will set it with boundary
  const headers = buildRequestHeaders(token, language, customHeaders, false);

  if (service !== 'chat') {
    throw new ApiError('File upload is only supported for chat service', 400, 'invalid_service');
  }

  const urlWithPath = buildChatRequestUrl(endpoint);

  try {
    const response = await fetch(urlWithPath, {
      body,
      method,
      headers,
    });
    return handleApiResponse(response, responseType);
  } catch (error: unknown) {
    return handleApiError(error, endpoint, method, body);
  }
};

// Default export of all API functions
const api = {
  apiRequest,
  authenticatedApiRequest,
  createMessageRequest,
  authenticatedFileUploadRequest,
};

export default api;
